import React from 'react';
import { Router, Switch, Route } from 'react-router-dom';
import './App.css';
import { BibleStudiesContainer } from './modules/bible-studies/containers/BibleStudiesContainer';
import { BibleStudyFormContainer } from './modules/bible-studies/containers/BibleStudyFormContainer';
import { EditBibleStudyFormContainer } from './modules/bible-studies/containers/EditBibleStudyFormContainer';
import { ViewBibleStudyContainer } from './modules/bible-studies/containers/ViewBibleStudyContainer';
import { CommunicationsContainer } from './modules/communications/containers/CommunicationsContainer';
import { PrivateRoute } from './modules/core/PrivateRoute';
import { Authorization } from './modules/core/rbac/Authorization';
import { EditEventFormContainer } from './modules/events/containers/EditEventFormContainer';
import { EventFormContainer } from './modules/events/containers/EventFormContainer';
import { EventsContainer } from './modules/events/containers/EventsContainer';
import { StreamOnThirdPartyContainer } from './modules/events/containers/StreamOnThirdPartyContainer';
import { ViewEventContainer } from './modules/events/containers/ViewEventContainer';
import { HomeContainer } from './modules/home/containers/HomeContainer';
import { AddMissionStatementContainer } from './modules/mission-statement/containers/AddMissionStatementContainer';
import { EditMissionStatementContainer } from './modules/mission-statement/containers/EditMissionStatementContainer';
import { MissionStatementContainer } from './modules/mission-statement/containers/MissionStatementContainer';
import { PeopleContainer } from './modules/people/containers/PeopleContainer';
import { ViewPersonContainer } from './modules/people/containers/ViewPersonContainer';
import { routes } from './route-list';
import { history } from './util/history';
import { ChurchContainer } from './modules/church/containers/ChurchContainer';
import { AddChurchContainer } from './modules/church/containers/AddChurchContainer';
import { EditChurchContainer } from './modules/church/containers/EditChurchContainer';
import { DonationsContainer } from './modules/donations/containers/DonationsContainer';
import { EmailVerificationScreen } from './modules/core/EmailVerificationScreen';
import { StudierRestrictedScreen } from './modules/core/StudierRestrictedScreen';
import { UserRequestsContainer } from './modules/user-requests/containers/UserRequestsContainer';
import { LoginContainer } from './modules/auth/containers/LoginContainer';
import { SignupContainer } from './modules/auth/containers/SignupContainer';
import { ForgotPasswordContainer } from './modules/auth/containers/ForgotPasswordContainer';
import { CallbackContainer } from './modules/auth/containers/CallbackContainer';
import { VerifyEmailContainer } from './modules/auth/containers/VerifyEmailContainer';
import { StripeSuccessRedirect } from './modules/donations/components/StripeSuccessRedirect';
import { StripeCancelRedirect } from './modules/donations/components/StripeCancelRedirect';

// Every role except 'Studier'
const EveryAdmin = Authorization(['Content Creator', 'Event Leader', 'Church Admin', 'System Admin']);

const Home = EveryAdmin(HomeContainer);
const People = EveryAdmin(PeopleContainer);
const ViewPerson = Authorization(['Church Admin', 'System Admin'])(ViewPersonContainer);
const BibleStudies = EveryAdmin(BibleStudiesContainer);
const AddBibleStudy = Authorization(['Content Creator', 'Church Admin', 'System Admin'])(BibleStudyFormContainer);
const ViewBibleStudy = EveryAdmin(ViewBibleStudyContainer);
const EditBibleStudy = Authorization(['Content Creator', 'Church Admin', 'System Admin'])(EditBibleStudyFormContainer);
const Events = EveryAdmin(EventsContainer);
const AddEvent = Authorization(['Event Leader', 'Church Admin', 'System Admin'])(EventFormContainer);
const ViewEvent = EveryAdmin(ViewEventContainer);
const EditEvent = Authorization(['Event Leader', 'Church Admin', 'System Admin'])(EditEventFormContainer);
const StreamEventOnThirdParty = Authorization(['Event Leader', 'Church Admin', 'System Admin'])(
  StreamOnThirdPartyContainer
);
const Communications = EveryAdmin(CommunicationsContainer);
const MissionStatement = Authorization(['System Admin', 'Church Admin'])(MissionStatementContainer);
const AddMissionStatement = Authorization(['System Admin', 'Church Admin'])(AddMissionStatementContainer);
const EditMissionStatement = Authorization(['System Admin', 'Church Admin'])(EditMissionStatementContainer);

const Church = EveryAdmin(ChurchContainer);
const AddChurch = EveryAdmin(AddChurchContainer);
const EditChurch = EveryAdmin(EditChurchContainer);
const Donations = Authorization(['Church Admin', 'System Admin'])(DonationsContainer);
const UserRequests = Authorization(['Church Admin', 'System Admin'])(UserRequestsContainer);

/**
 * Routes are structured here, and declared at route-list.js
 */
const App = () => {
  return (
    <Router history={history}>
      <Switch>
        {/* Public auth routes */}
        <Route exact path={routes.LOGIN} component={LoginContainer} />
        <Route exact path={routes.AUTH_SIGNUP} component={SignupContainer} />
        <Route exact path={routes.AUTH_FORGOT_PASSWORD} component={ForgotPasswordContainer} />
        <Route exact path={routes.AUTH_CALLBACK} component={CallbackContainer} />
        <Route exact path={routes.AUTH_VERIFY_EMAIL} component={VerifyEmailContainer} />
        <Route exact path={routes.EMAIL_VERIFICATION} component={EmailVerificationScreen} />
        <Route exact path={routes.STUDIER_RESTRICTED} component={StudierRestrictedScreen} />
        <Route exact path={routes.DONATION_SUCCESS} component={StripeSuccessRedirect} />
        <Route exact path={routes.DONATION_CANCEL} component={StripeCancelRedirect} />
        <PrivateRoute exact path={routes.HOME} component={Home} />

        {/* Church */}
        <PrivateRoute exact path={routes.CHURCH} component={Church} />
        <PrivateRoute path={routes.CHURCH__ADD} component={AddChurch} />
        <PrivateRoute path={routes.CHURCH__EDIT()} component={EditChurch} />

        {/* People */}
        <PrivateRoute exact path={routes.PEOPLE} component={People} />
        <PrivateRoute path={routes.PEOPLE__VIEW()} component={ViewPerson} />

        {/* Bible Studies */}
        <PrivateRoute exact path={routes.BIBLE_STUDIES} component={BibleStudies} />
        <PrivateRoute path={routes.BIBLE_STUDIES__ADD} component={AddBibleStudy} />
        <PrivateRoute exact path={routes.BIBLE_STUDIES__VIEW()} component={ViewBibleStudy} />
        <PrivateRoute exact path={routes.BIBLE_STUDIES__EDIT()} component={EditBibleStudy} />

        {/* Events */}
        <PrivateRoute exact path={routes.EVENTS} component={Events} />
        <PrivateRoute path={routes.EVENTS__ADD} component={AddEvent} />
        <PrivateRoute exact path={routes.EVENTS__VIEW()} component={ViewEvent} />
        <PrivateRoute path={routes.EVENTS__EDIT()} component={EditEvent} />
        {/* This screen has webcam access. We won't be using this. */}
        {/* <PrivateRoute path={routes.EVENTS__START()} component={StartEventContainer} /> */}
        <PrivateRoute path={routes.EVENTS__STREAM_EVENT_ON_THIRD_PARTY()} component={StreamEventOnThirdParty} />

        {/* Communications */}
        <PrivateRoute exact path={routes.COMMUNICATIONS} component={Communications} />

        {/* Mission Statement */}
        <PrivateRoute exact path={routes.MISSION_STATEMENT} component={MissionStatement} />
        <PrivateRoute path={routes.MISSION_STATEMENT__ADD} component={AddMissionStatement} />
        <PrivateRoute path={routes.MISSION_STATEMENT__EDIT()} component={EditMissionStatement} />

        {/* Donations */}
        <PrivateRoute exact path={routes.DONATIONS} component={Donations} />

        {/* User Requests */}
        <PrivateRoute exact path={routes.USER_REQUESTS} component={UserRequests} />
      </Switch>
    </Router>
  );
};

export default App;
